/* eslint-disable no-nested-ternary */
import { Currencies } from '@constants/enums';
import { Alert, Typography } from '@mui/material';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import useBankAccountFields from '../../hooks/useBankAccountFields';
import { fieldsByCurrency } from '../../constants';
import LoadingInstructions from './LoadingInstructions';
import InstructionField from './InstructionField';
import { IntermediaryBankField } from './IntermediaryBankField';
import type { InstructionsProps } from '../../types';
import InstructionsStyles from './Instructions.style';

const Instructions = ({
  instructions,
  loading,
  currency,
  onCopy,
}: InstructionsProps) => {
  const { t } = useTranslation();

  const {
    account_number: accountNumber,
    account_type: accountType,
    bank_name: bankName,
    bank_address: bankAddress,
    beneficiary,
    beneficiary_address: beneficiaryAddress,
    routing_code: routingCode,
    swift_code: swiftCode,
    transfer_method: transferMethod,
    name,
    intermediaryBank,
  } = instructions || {};

  const { accountFieldKey } = useBankAccountFields({
    currency,
    instructions,
  });

  const activeFields =
    fieldsByCurrency[currency as keyof typeof fieldsByCurrency] || [];

  const validateFieldType = (field: string | string[]): string => {
    if (Array.isArray(field)) {
      return field
        .map((method) =>
          t([`aiMethod${method.toUpperCase()}`, method.toUpperCase()])
        )
        .join(', ');
    }

    if (typeof field !== 'string') {
      return String(field);
    }

    try {
      if (
        (field.trim().startsWith('[') || field.trim().startsWith('{')) &&
        field.includes("'")
      ) {
        const parsedData = JSON.parse(field.replace(/'/g, '"'));
        if (Array.isArray(parsedData)) {
          return parsedData
            .map((method: string) =>
              t([`aiMethod${method.toUpperCase()}`, method.toUpperCase()])
            )
            .join(', ');
        }
        return String(parsedData);
      }
    } catch (error) {
      console.error('Error parsing JSON', error);
    }
    return field;
  };

  const renderMainInstructions = () => {
    if (loading) return <LoadingInstructions />;

    return (
      <>
        {transferMethod && activeFields.includes('transferMethod') && (
          <InstructionField
            label={t('afnTransferMethod')}
            value={validateFieldType(transferMethod)}
            translationKey="afnTransferMethod"
            showCopyButton={false}
            t={t}
            onCopy={onCopy}
          />
        )}

        {accountNumber && activeFields.includes('accountNumber') && (
          <InstructionField
            label={t(accountFieldKey)}
            value={accountNumber}
            translationKey={accountFieldKey}
            t={t}
            onCopy={onCopy}
          />
        )}

        {accountType && activeFields.includes('accountType') && (
          <InstructionField
            label={t('afnAccountType')}
            value={validateFieldType(accountType)}
            translationKey="afnAccountType"
            showCopyButton={false}
            t={t}
            onCopy={onCopy}
          />
        )}

        {routingCode &&
          routingCode[0] &&
          activeFields.includes('routingCode') && (
            <InstructionField
              label={t(
                currency === Currencies.USD
                  ? 'afnAchRoutingNumber'
                  : 'afnTransitNumber'
              )}
              value={routingCode[0]}
              translationKey={
                currency === Currencies.USD
                  ? 'afnAchRoutingNumber'
                  : 'afnTransitNumber'
              }
              t={t}
              onCopy={onCopy}
            />
          )}

        {routingCode &&
          routingCode[1] &&
          activeFields.includes('routingCode') && (
            <InstructionField
              label={t(
                currency === Currencies.USD
                  ? 'afnWireRoutingNumber'
                  : 'afnInstitutionNumber'
              )}
              value={routingCode[1]}
              translationKey={
                currency === Currencies.USD
                  ? 'afnWireRoutingNumber'
                  : 'afnInstitutionNumber'
              }
              t={t}
              onCopy={onCopy}
            />
          )}

        {beneficiary && activeFields.includes('beneficiary') && (
          <InstructionField
            label={t('afnBeneficiary')}
            value={beneficiary}
            translationKey="afnBeneficiary"
            t={t}
            onCopy={onCopy}
          />
        )}

        {beneficiaryAddress && activeFields.includes('beneficiaryAddress') && (
          <InstructionField
            label={t('afnBeneficiaryAddress')}
            value={beneficiaryAddress}
            translationKey="afnBeneficiaryAddress"
            t={t}
            onCopy={onCopy}
          />
        )}

        {(bankName ?? name) && activeFields.includes('bankName') && (
          <InstructionField
            label={t('afnBeneficiaryBank')}
            value={bankName ?? name ?? ''}
            translationKey="afnBeneficiaryBank"
            t={t}
            onCopy={onCopy}
          />
        )}

        {bankAddress && activeFields.includes('bankAddress') && (
          <InstructionField
            label={t('afnBankAddress')}
            value={bankAddress}
            translationKey="afnBankAddress"
            t={t}
            onCopy={onCopy}
          />
        )}

        {currency && (
          <InstructionField
            label={t('afnCurrency')}
            value={currency}
            translationKey="afnCurrency"
            showCopyButton={false}
            t={t}
            onCopy={onCopy}
          />
        )}
      </>
    );
  };

  const renderInternationalPayment = () => {
    if (!swiftCode && !intermediaryBank) return null;

    return (
      <>
        <Typography className="!my-4">{t('afnSwiftCodeTitle')}</Typography>

        {intermediaryBank && (
          <Alert
            className={InstructionsStyles.internationalPaymentAlert}
            severity="info"
          >
            <Typography component="p" variant="caption">
              {t('afnInternationalPaymentInfo')}
            </Typography>
          </Alert>
        )}

        <div className={InstructionsStyles.card}>
          {intermediaryBank ? (
            <div className={InstructionsStyles.internationalPayment}>
              {Object.entries(intermediaryBank).map(([key, value]) => (
                <IntermediaryBankField
                  key={key}
                  fieldKey={key}
                  value={value}
                  t={t}
                  onCopy={onCopy}
                />
              ))}
            </div>
          ) : (
            swiftCode && (
              <InstructionField
                label={t('afnSwiftCode')}
                value={swiftCode}
                translationKey="afnSwiftCode"
                t={t}
                onCopy={onCopy}
              />
            )
          )}
        </div>

        <Alert className={InstructionsStyles.alert} severity="info">
          <Typography component="p" variant="caption">
            {t('afnDisclaimer1')}
          </Typography>
          <Typography component="p" variant="caption">
            {t('afnDisclaimer2')}
          </Typography>
        </Alert>
      </>
    );
  };

  return (
    <div>
      <Typography
        className={InstructionsStyles.instructionsTitle}
        variant="body2"
      >
        {t('afnInstructionsTitle')}
      </Typography>
      <div
        className={clsx(
          InstructionsStyles.card,
          InstructionsStyles.cardSeparator
        )}
      >
        {renderMainInstructions()}
      </div>
      {renderInternationalPayment()}
    </div>
  );
};

export default Instructions;
